import React, { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Trash2, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import './RetentionNotice.css';

// One-time notice so a new account learns its uploads auto-delete before any
// document actually disappears. Shown once per account (retention_notice_ack),
// only while a retention window is set. Dismissing flags the account so it
// never nags again - same shape as the onboarding tour.
function RetentionNotice() {
  const { t } = useTranslation('common');
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const days = user?.auto_delete_days ?? null;
  // Wait for the onboarding tour to finish first so a new account never gets
  // two modals stacked on top of each other.
  const show = !!user && days != null && user.tour_completed && !user.retention_notice_ack;

  const ack = useCallback(() => {
    updateUser({ retention_notice_ack: true });
    api.updateUser(user.id, { retention_notice_ack: true }).catch((err) => {
      console.error('Failed to persist retention notice ack:', err);
    });
  }, [user, updateUser]);

  if (!show) return null;

  // Earliest content (uploaded around signup) is due at created_at + window.
  const created = new Date(
    user.created_at?.endsWith('Z') ? user.created_at : `${user.created_at}Z`,
  );
  const dueDate = new Date(created.getTime() + days * 24 * 60 * 60 * 1000);
  const daysLeft = Math.max(0, Math.ceil((dueDate - new Date()) / (24 * 60 * 60 * 1000)));
  const dateStr = dueDate.toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' });

  const goToSettings = () => {
    ack();
    navigate('/settings');
  };

  return createPortal(
    <div className="retention-notice-overlay" role="dialog" aria-modal="true">
      <div className="retention-notice-card">
        <div className="retention-notice-icon">
          <Trash2 size={22} />
        </div>
        <h2 className="retention-notice-title">{t('retentionNotice.title', { days })}</h2>
        <p className="retention-notice-body">
          {t('retentionNotice.body', { days, daysLeft, date: dateStr })}
        </p>
        <p className="retention-notice-hint">{t('retentionNotice.hint')}</p>
        <div className="retention-notice-actions">
          <button type="button" className="retention-notice-btn ghost" onClick={goToSettings}>
            <Settings size={16} />
            {t('retentionNotice.settings')}
          </button>
          <button type="button" className="retention-notice-btn primary" onClick={ack}>
            {t('retentionNotice.ok')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default RetentionNotice;
